import apiClient from './api';

// Response interface for vendor application endpoints (VENDOR_APPLICATIONS table)
export interface VendorApplicationResponse {
  success?: boolean;
  message?: string;
  error?: string;
  applicationId?: string;
  application?: {
    applicationId: string;
    businessName: string;
    contactName: string;
    email: string;
    phone: string;
    businessType: string;
    status: string;
    submittedAt: string;
  };
}

// Pagination interface
export interface ApplicationsPagination {
  hasMore: boolean;
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

// Response type that includes both applications and pagination
export interface ApplicationsResponse {
  applications: VendorApplication[];
  pagination: ApplicationsPagination;
}

// API Response interface for applications list (matches actual API format)
export interface ApplicationsApiResponse {
  success: boolean;
  message: string;
  applications: any[];
  pagination: ApplicationsPagination;
}

// Frontend interface for vendor applications
export interface VendorApplication {
  id: string;
  applicationId: string;
  businessName: string;
  businessType: string;
  category: string; // Alias for businessType
  contactName: string;
  email: string;
  phone: string;
  hasLicense: boolean;
  createdAt: string;
  submittedAt: string;
  status: 'pending' | 'approved' | 'info_requested';
  infoRequests?: Array<{
    requestId: string;
    message: string;
    requestedBy: string;
    timestamp: string;
  }>;
}

export interface ApproveVendorRequest {
  vendorId: string;
}

export interface RejectVendorRequest {
  vendorId: string;
  reason?: string;
}

// Helper function to transform API response to frontend format
const transformApplication = (app: any): VendorApplication => {
  return {
    id: app.applicationId || app.id,
    applicationId: app.applicationId || app.id,
    businessName: app.businessName,
    businessType: app.businessType,
    category: app.businessType, // Use businessType as category
    contactName: app.contactName,
    email: app.email,
    phone: app.phone,
    hasLicense: app.hasLicense ?? false,
    createdAt: app.submittedAt || app.createdAt,
    submittedAt: app.submittedAt,
    status: app.status,
    infoRequests: app.infoRequests || [],
  };
};

// Helper function to extract pagination from API response
const extractPagination = (data: any, page: number): ApplicationsPagination => {
  // API format: { success: true, message: "...", applications: [...], pagination: {...} }
  if (data?.pagination) {
    return {
      hasMore: data.pagination.hasMore ?? (data.pagination.currentPage < data.pagination.totalPages),
      currentPage: data.pagination.currentPage ?? page,
      totalItems: data.pagination.totalItems ?? 0,
      totalPages: data.pagination.totalPages ?? 1,
    };
  }
  
  // Default pagination if not provided
  return {
    hasMore: false,
    currentPage: page,
    totalItems: 0,
    totalPages: 1,
  };
};

export const applicationsService = {
  // Get pending applications
  getPendingApplications: async (page: number = 1, limit: number = 20): Promise<ApplicationsResponse> => {
    const response = await apiClient.get(`/pending-applications?limit=${limit}&page=${page}`);
    const data = response.data;
    console.log("📊 Pending Applications: Response", data);
    
    // Handle nested structure: { success, message, data: { success, applications, pagination } }
    const actualData = data?.data || data;
    
    // API format: { success: true, message: "...", applications: [...], pagination: {...} }
    if (actualData.success && Array.isArray(actualData.applications)) {
      const applications = actualData.applications.map(transformApplication);
      const pagination = extractPagination(actualData, page);
      return { applications, pagination };
    }
    
    // Fallback for unexpected structure
    console.warn('📦 Unexpected response structure for pending applications:', data);
    return {
      applications: [],
      pagination: {
        hasMore: false,
        currentPage: page,
        totalItems: 0,
        totalPages: 1,
      },
    };
  },

  // Get approved applications
  getApprovedApplications: async (page: number = 1, limit: number = 20): Promise<ApplicationsResponse> => {
    console.log("📊 Approved Applications: Request", page, limit);
    const response = await apiClient.get(`/approved-applications?limit=${limit}&page=${page}`);
    const data = response.data;
    console.log("📊 Approved Applications: Response", response);
    
    // Handle nested structure: { success, message, data: { success, applications, pagination } }
    const actualData = data?.data || data;
    
    // API format: { success: true, message: "...", applications: [...], pagination: {...} }
    if (actualData.success && Array.isArray(actualData.applications)) {
      const applications = actualData.applications.map(transformApplication);
      const pagination = extractPagination(actualData, page);
      return { applications, pagination };
    }
    
    // Fallback for unexpected structure
    console.warn('✅ Unexpected response structure for approved applications:', data);
    return {
      applications: [],
      pagination: {
        hasMore: false,
        currentPage: page,
        totalItems: 0,
        totalPages: 1,
      },
    };
  },

  // Get rejected applications
  getRejectedApplications: async (page: number = 1, limit: number = 20): Promise<ApplicationsResponse> => {
    const response = await apiClient.get(`/rejected-applications?limit=${limit}&page=${page}`);
    const data = response.data;
    
    // Handle nested structure: { success, message, data: { success, applications, pagination } }
    const actualData = data?.data || data;
    
    // API format: { success: true, message: "...", applications: [...], pagination: {...} }
    if (actualData.success && Array.isArray(actualData.applications)) {
      const applications = actualData.applications.map(transformApplication);
      const pagination = extractPagination(actualData, page);
      return { applications, pagination };
    }
    
    // Fallback for unexpected structure
    console.warn('❌ Unexpected response structure for rejected applications:', data);
    return {
      applications: [],
      pagination: {
        hasMore: false,
        currentPage: page,
        totalItems: 0,
        totalPages: 1,
      },
    };
  },

  // Approve vendor
  approveVendor: async (applicationId: string): Promise<VendorApplicationResponse> => {
    const response = await apiClient.post('/approve-vendor', { applicationId });
    return response.data;
  },

  // Reject vendor
  rejectVendor: async (applicationId: string, reason?: string): Promise<VendorApplicationResponse> => {
    const response = await apiClient.post('/reject-vendor', { applicationId, reason });
    return response.data;
  },

  // Request more info from vendor
  requestMoreInfo: async (applicationId: string, message: string): Promise<VendorApplicationResponse> => {
    const response = await apiClient.post('/request-more-info', { applicationId, message });
    return response.data;
  },

  // Get info requested applications (cursor-based)
  getInfoRequestedApplicationsCursor: async (afterCursor?: string, limit: number = 8): Promise<{ applications: VendorApplication[]; endCursor?: string }> => {
    const url = afterCursor 
      ? `/info-requested-applications?limit=${limit}&afterCursor=${encodeURIComponent(afterCursor)}`
      : `/info-requested-applications?limit=${limit}`;
    
    const response = await apiClient.get(url);
    const data = response.data;
    
    if (data.success && data.data) {
      return {
        applications: data.data.items.map(transformApplication),
        endCursor: data.data.endCursor
      };
    }
    
    return { applications: [], endCursor: undefined };
  },

  // Get info requested applications (cursor-based)
  getInfoRequestedApplications: async (page: number = 1, limit: number = 20): Promise<ApplicationsResponse> => {
    const response = await apiClient.get(`/info-requested-applications?limit=${limit}`);
    const data = response.data;
    
    // Handle double-nested structure from BaseHandler
    // Response: { success, message, data: { items: [...], endCursor: "..." }, timestamp }
    const actualData = data?.data || data;
    
    // API format: { items: [...], endCursor: "..." }
    if (actualData && Array.isArray(actualData.items)) {
      const applications = actualData.items.map(transformApplication);
      return { 
        applications, 
        pagination: {
          hasMore: !!actualData.endCursor,
          currentPage: page,
          totalPages: 1, // Cursor pagination doesn't have total pages
          totalItems: actualData.items.length // Number of items in current page
        }
      };
    }
    
    // Fallback for unexpected structure
    console.warn('⚠️ Unexpected response structure for info requested applications:', data);
    return {
      applications: [],
      pagination: {
        hasMore: false,
        currentPage: page,
        totalItems: 0,
        totalPages: 1,
      },
    };
  },
};

export default applicationsService;

